import { useEffect, useRef, useState } from "react";
import { CodeInput } from "./components/codeInput";
import { TreasurePage } from "./components/TreasurePage";
import radioactiveSymbol from "../assets/radioactive-symbol.png";
import "./index.css";

// Get app URL
const appURL = process.env.BUN_PUBLIC_WEBSITE_URL ?? "http://localhost:3000";

type Status = { type: "success" | "error" | null; message: string };

// Main app frontend
export function App() {
  const [gotData, setGotData] = useState(false);
  const fetchedRef = useRef(false);

  // Get the number of required codes
  const [numCodes, setNumCodes] = useState(0);
  const [expectedCodesLengths, setExpectedCodesLengths] = useState<number[]>([]);
  const [codes, setCodes] = useState<string[]>([]);
  async function getNumCodes() {
    const data = await (await fetch(`${appURL}/api/data`, { method: "GET" })).json();
    if (data.code == 200) {
      setExpectedCodesLengths(data.codesLengths)
      setNumCodes(data.codesLengths.length)
      setCodes(Array.from({ length: data.codesLengths.length }, () => ""));
      setGotData(true);
    }
  }
  
  // Handle input changes
  function handleCodeChange(index: number, value: string) {
    setCodes((prev) => prev.map((code, i) => (i === index ? value : code)));
    setAccessGranted(false);
  }

  // Validate the entered codes against the server
  const [isValidating, setIsValidating] = useState(false);
  const [status, setStatus] = useState<Status>({ type: null, message: "" });
  const [accessGranted, setAccessGranted] = useState(false);
  const [treasureToken, setTreasureToken] = useState<string | null>(null);
  const [showTreasure, setShowTreasure] = useState(false);
  async function validate() {
    setIsValidating(true);
    setStatus({ type: null, message: "" });
    try {
      const res = await fetch(`${appURL}/api/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codes: codes }),
      });
      const data = await res.json();
      const granted = data.code == 200;
      setAccessGranted(granted);
      setTreasureToken(granted ? data.token ?? null : null);
      setStatus(granted
        ? { type: "success", message: data.message }
        : { type: "error", message: data.message });
    } catch {
      setStatus({ type: "error", message: "Erreur de connexion au système." });
    } finally {
      setIsValidating(false);
    }
  }

  // Listeners
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    getNumCodes();
  }, []);

  // If the codes were correct
  if (showTreasure) {
    return <TreasurePage onBack={() => setShowTreasure(false)} token={treasureToken} />;
  }

  return (
    <div className="relative z-10 w-full max-w-2xl mx-auto p-8 flex flex-col items-center">
      {/* Hazard warning bands */}
      <div className="hazard-stripes fixed top-0 left-0 right-0 h-4 z-20" />
      <div className="hazard-stripes fixed bottom-0 left-0 right-0 h-4 z-20" />

      {/* Header warning plaque */}
      <div className="flicker flex items-center gap-4 border-2 border-[#f5c518] bg-[#1a1a1a]/90 px-6 py-4 mb-8 w-full">
        <img
          src={radioactiveSymbol}
          alt="Symbole radioactif"
          className="radiation-animate radioactive-yellow w-12 h-12 shrink-0 opacity-90"
        />
        <div className="flex-1 text-left">
          <p className="text-3xl font-bold text-[#f5c518] uppercase tracking-widest leading-tight">
            Page verrouillée
          </p>
          <p className="text-sm text-[#9a9a85] uppercase tracking-[0.2em] mt-1">
            Zone contaminée — accès restreint
          </p>
        </div>
      </div>

      <p className="mb-6 text-[#9a9a85] text-sm uppercase tracking-[0.3em]">
        Tous les codes doivent être corrects pour débloquer cette page
      </p>

      {/* Control panel */}
      <div className="w-full border border-[#3a3f2a] bg-[#14170f]/80 p-6 space-y-5 relative">
        <div className="absolute -top-3 left-6 uppercase text-[10px] tracking-[0.3em] text-[#39ff14]/80 bg-[#0a0d08] px-2">
          Console d'accès
        </div>

        {gotData ? (
          Array.from({ length: numCodes }, (_, i) => (
            <CodeInput key={i} value={codes[i]!} onChange={(value) => handleCodeChange(i, value)} expectedLength={expectedCodesLengths[i]!}/>
          ))
        ) : (
          <p className="text-center text-[#9a9a85] animate-pulse">Chargement...</p>
        )}

        {status.type && (
          <div className={`border px-4 py-3 text-sm uppercase tracking-widest text-center ${
            status.type === "success"
              ? "border-[#39ff14] text-[#39ff14] bg-[#39ff14]/10"
              : "border-red-500 text-red-400 bg-red-500/10"
          }`}>
            {status.message}
          </div>
        )}

        {accessGranted ? (
          <button
            onClick={() => setShowTreasure(true)}
            className="danger-glow w-full uppercase font-bold tracking-[0.3em] text-lg text-black bg-[#39ff14] hover:bg-[#7bff5c] px-6 py-3 transition-colors"
          >
            Accéder
          </button>
        ) : (
          <button
            onClick={validate}
            disabled={isValidating}
            className="danger-glow w-full uppercase font-bold tracking-[0.3em] text-lg text-black bg-[#f5c518] hover:bg-[#ffd93d] disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3 transition-colors"
          >
            {isValidating ? "Vérification..." : "Valider"}
          </button>
        )}
      </div>

      <p className="mt-6 text-[10px] uppercase tracking-[0.4em] text-[#55554a]">
        Site nucléaire · poste 04
      </p>
    </div>
  );
}

export default App;
